import { CURRENT_USER } from '@src/queries/CurrentUserQuery'
import { Router, loginRoute, homeRoute } from '@src/routes'
import { getLocalizedUrl } from '@utils/regional'
import { userSession } from '@src/utils/userSession'
import { initApolloClient } from '@src/lib/WithApollo'
import getCookiesFromContext from './getCookiesFromContext'
import { UserType } from '@src/types/graphql-types'
import { CurrentUserQuery } from '@src/queries/CurrentUserQuery.generated'
import { isServer } from './isServer'

interface RequireAuthenticationConfig {
  allowRememberMeUsers?: boolean
}

const requireAuthentication =
  ({ allowRememberMeUsers = false }: RequireAuthenticationConfig = {}) =>
  async (ctx: ExtendedNextPageContext) => {
    const cookies = getCookiesFromContext(ctx)
    const apolloClient = initApolloClient(cookies)

    let data
    try {
      // Ensure we get the latest currentUser result from the server
      const result = await apolloClient.query({
        query: CURRENT_USER,
        fetchPolicy: 'network-only',
      })
      data = result.data
    } catch (e) {}

    const currentUser: CurrentUserQuery['me'] = data?.me

    // @TODO if this GIP utility gets used alongside others,
    // this should pass through props like `return { ...props }`

    const allowedUserTypes: UserType[] = [
      UserType.LoggedIn,
      ...(allowRememberMeUsers ? [UserType.RememberMe] : []),
    ]

    if (allowedUserTypes.includes(currentUser?.type)) {
      return {}
    }

    // Ensure the redirectURL is prepended with a basename
    const { as, href } = getLocalizedUrl(
      userSession(cookies.getAll()),
      `${loginRoute.index()}?redirect=${encodeURIComponent(
        ctx.asPath || homeRoute.index()
      )}&sessionExpired=true`
    )

    // Use server-side redirection when in SSR
    if (isServer() && ctx.res) {
      ctx.res.writeHead(302, {
        Location: as,
      })
      ctx.res.end()

      // Return an error so the page that called, `requireAuthentication` can prevent rendering
      return { errorStatus: 401 }
    }

    // Otherwise, use the client router
    await Router.push(href, as)

    // Return an error so the page that called, `requireAuthentication` can prevent rendering
    return { errorStatus: 401 }
  }

export default requireAuthentication
